import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';

const JobDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showReferModal, setShowReferModal] = useState(false);
  
  // Form states
  const [application, setApplication] = useState({
    coverLetter: '',
    resume: null
  });
  
  const [referral, setReferral] = useState({
    name: '',
    email: '',
    relationship: '',
    note: ''
  });

  // Check if the URL has the apply=true parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('apply') === 'true') {
      setShowApplyModal(true);
    }
  }, [location]);

  useEffect(() => {
    // Mock data for jobs
    const jobs = [
      {
        id: 1,
        title: 'Investment Banking Associate',
        company: 'Goldman Sachs',
        location: 'New York, NY',
        type: 'Full-time',
        salary: '$120,000 - $150,000',
        description: 'Goldman Sachs is seeking an Investment Banking Associate to join our M&A team. The ideal candidate will have 2-3 years of experience in investment banking or related fields.',
        detailedDescription: 'As an Investment Banking Associate at Goldman Sachs, you will work on a variety of transactions including mergers and acquisitions, leveraged buyouts, and capital raising activities. You will be responsible for financial modeling, valuation analysis, industry research, and client presentations. This role offers excellent opportunities for career growth and development in the investment banking industry.',
        responsibilities: [
          'Conduct financial analysis and valuation of companies',
          'Prepare client presentations and pitch materials',
          'Perform industry research and competitive analysis',
          'Build and maintain financial models',
          'Support senior bankers in deal execution',
          'Interact with clients and provide transaction support'
        ],
        requirements: [
          'Bachelor\'s degree in Finance, Economics, or related field',
          '2-3 years of experience in investment banking',
          'Strong financial modeling skills',
          'Excellent communication and presentation abilities',
          'Proficiency in Excel, PowerPoint, and financial analysis tools',
          'Ability to work in a fast-paced environment with tight deadlines'
        ],
        benefits: [
          'Competitive salary and bonus structure',
          'Comprehensive health, dental, and vision insurance',
          '401(k) matching program',
          'Professional development opportunities',
          'Wellness programs',
          'Paid time off and parental leave'
        ],
        postedDate: '2025-04-15',
        applicationDeadline: '2025-05-15',
        featured: true,
        contactPerson: 'HR Department',
        contactEmail: 'careers@goldmansachs.com'
      },
      {
        id: 2,
        title: 'Private Equity Analyst',
        company: 'Blackstone',
        location: 'New York, NY',
        type: 'Full-time',
        salary: '$100,000 - $130,000',
        description: 'Blackstone is looking for a Private Equity Analyst to join our team. The role involves financial analysis, due diligence, and portfolio company monitoring.',
        detailedDescription: 'As a Private Equity Analyst at Blackstone, you will be involved in all aspects of the investment process, from deal sourcing and evaluation to due diligence and portfolio management. You will work closely with senior investment professionals to analyze potential investment opportunities, conduct financial modeling, and monitor existing portfolio companies. This role provides excellent exposure to various industries and transaction types.',
        responsibilities: [
          'Conduct financial analysis of potential investment opportunities',
          'Perform due diligence on target companies',
          'Build and maintain financial models',
          'Monitor performance of portfolio companies',
          'Prepare investment committee materials',
          'Conduct industry and market research'
        ],
        requirements: [
          'Bachelor\'s degree in Finance, Business, or related field',
          '1-2 years of experience in investment banking or private equity',
          'Strong analytical and financial modeling skills',
          'Excellent attention to detail',
          'Proficiency in Excel and PowerPoint',
          'Strong written and verbal communication skills'
        ],
        benefits: [
          'Competitive salary and performance-based bonus',
          'Comprehensive benefits package',
          '401(k) plan with employer matching',
          'Professional development opportunities',
          'Wellness programs',
          'Collaborative work environment'
        ],
        postedDate: '2025-04-12',
        applicationDeadline: '2025-05-12',
        featured: true,
        contactPerson: 'Talent Acquisition Team',
        contactEmail: 'careers@blackstone.com'
      },
      {
        id: 3,
        title: 'M&A Tax Manager',
        company: 'Deloitte',
        location: 'Chicago, IL',
        type: 'Full-time',
        salary: '$110,000 - $140,000',
        description: 'Deloitte is seeking an experienced M&A Tax Manager to provide tax consulting services to clients involved in mergers, acquisitions, and divestitures.',
        detailedDescription: 'As an M&A Tax Manager at Deloitte, you will lead tax due diligence projects, structure optimization initiatives, and post-transaction integration planning. You will work with a diverse client base including private equity firms, corporations, and investment banks to navigate complex tax issues in M&A transactions.',
        responsibilities: [
          'Lead tax due diligence for M&A transactions',
          'Develop tax-efficient transaction structures',
          'Manage client relationships and project teams',
          'Prepare tax analysis and recommendations',
          'Coordinate with other advisory teams',
          'Review and approve junior staff work'
        ],
        requirements: [
          'Bachelor\'s degree in Accounting, Finance, or related field',
          'CPA certification',
          '5+ years of experience in tax consulting with M&A focus',
          'Strong knowledge of federal and state tax regulations',
          'Excellent project management skills'
        ],
        benefits: [
          'Competitive salary and performance bonus',
          'Comprehensive health and wellness benefits',
          'Professional development and training programs',
          'Flexible work arrangements',
          'Retirement savings plan with company match'
        ],
        postedDate: '2025-04-10',
        applicationDeadline: '2025-05-10',
        featured: false,
        contactPerson: 'Tax Recruiting Team',
        contactEmail: 'careers@deloitte.com'
      },
      {
        id: 4,
        title: 'Corporate Development Associate',
        company: 'Microsoft',
        location: 'Redmond, WA',
        type: 'Full-time',
        salary: '$115,000 - $145,000',
        description: 'Microsoft is looking for a Corporate Development Associate to support our M&A activities. The role involves target identification, valuation, due diligence, and deal execution.',
        detailedDescription: 'As a Corporate Development Associate at Microsoft, you will play a key role in identifying and evaluating strategic acquisition opportunities that align with our business objectives. You will work closely with senior leadership to execute transactions that drive growth and innovation across our technology platforms.',
        responsibilities: [
          'Identify and evaluate potential acquisition targets',
          'Conduct financial modeling and valuation analysis',
          'Support due diligence processes',
          'Prepare materials for executive leadership',
          'Coordinate with legal, finance, and business teams',
          'Monitor industry trends and competitive landscape'
        ],
        requirements: [
          'Bachelor\'s degree in Finance, Business, or related field',
          '2-4 years of experience in investment banking or corporate development',
          'Strong financial modeling and valuation skills',
          'Excellent analytical and communication abilities',
          'Experience with technology sector preferred'
        ],
        benefits: [
          'Competitive salary and equity compensation',
          'Comprehensive health, dental, and vision coverage',
          'Generous vacation and parental leave policies',
          'Professional development opportunities',
          'Employee stock purchase plan'
        ],
        postedDate: '2025-04-08',
        applicationDeadline: '2025-05-08',
        featured: false,
        contactPerson: 'Corporate Development Team',
        contactEmail: 'corpdev@microsoft.com'
      },
      {
        id: 5,
        title: 'Investment Banking Summer Analyst',
        company: 'JP Morgan',
        location: 'New York, NY',
        type: 'Internship',
        salary: '$85,000 - $95,000 (pro-rated)',
        description: 'JP Morgan is seeking Summer Analysts for our Investment Banking division. This is a 10-week program designed to provide hands-on experience in financial analysis, client presentations, and deal execution.',
        detailedDescription: 'Our Summer Analyst program offers unparalleled exposure to the investment banking industry. You will work alongside experienced professionals on live transactions, develop technical skills, and build relationships that will serve as the foundation for your career in finance.',
        responsibilities: [
          'Support execution of M&A transactions and capital raises',
          'Conduct financial analysis and modeling',
          'Prepare client presentations and pitch materials',
          'Perform industry and company research',
          'Participate in client meetings and calls',
          'Complete training modules and networking events'
        ],
        requirements: [
          'Currently pursuing a Bachelor\'s degree in Finance, Economics, or related field',
          'Expected graduation date in 2026',
          'Strong academic record',
          'Excellent analytical and communication skills',
          'Proficiency in Excel and PowerPoint'
        ],
        benefits: [
          'Competitive internship compensation',
          'Housing stipend for eligible participants',
          'Comprehensive training program',
          'Mentorship and networking opportunities',
          'Potential for full-time offer'
        ],
        postedDate: '2025-04-05',
        applicationDeadline: '2025-05-05',
        featured: false,
        contactPerson: 'Campus Recruiting Team',
        contactEmail: 'campus.recruiting@jpmorgan.com'
      },
      {
        id: 6,
        title: 'Valuation Associate',
        company: 'KPMG',
        location: 'Boston, MA',
        type: 'Full-time',
        salary: '$90,000 - $120,000',
        description: 'KPMG is seeking a Valuation Associate to join our Valuation Services team. The role involves performing business and asset valuations for M&A, financial reporting, and tax purposes.',
        detailedDescription: 'As a Valuation Associate at KPMG, you will work on diverse valuation engagements across multiple industries. You will develop expertise in various valuation methodologies and work with sophisticated clients on complex valuation challenges.',
        responsibilities: [
          'Perform business and asset valuations',
          'Conduct market research and analysis',
          'Build and maintain valuation models',
          'Prepare valuation reports and presentations',
          'Support senior team members on client engagements',
          'Participate in client meetings and discussions'
        ],
        requirements: [
          'Bachelor\'s degree in Finance, Accounting, or related field',
          '1-3 years of experience in valuation or related field',
          'Strong financial modeling skills',
          'Knowledge of valuation methodologies',
          'CFA or ASA designation preferred'
        ],
        benefits: [
          'Competitive salary and bonus structure',
          'Comprehensive benefits package',
          'Professional development and certification support',
          'Flexible work arrangements',
          'Collaborative team environment'
        ],
        postedDate: '2025-04-03',
        applicationDeadline: '2025-05-03',
        featured: false,
        contactPerson: 'Valuation Services Recruiting',
        contactEmail: 'valuation.careers@kpmg.com'
      },
      {
        id: 7,
        title: 'Corporate Attorney - M&A',
        company: 'Skadden',
        location: 'New York, NY',
        type: 'Full-time',
        salary: '$190,000 - $250,000',
        description: 'Skadden is seeking an experienced Corporate Attorney to join our M&A practice. The role involves advising clients on mergers, acquisitions, divestitures, and other corporate transactions.',
        detailedDescription: 'As a Corporate Attorney in our M&A practice, you will work on high-profile transactions for Fortune 500 companies, private equity firms, and other sophisticated clients. You will have the opportunity to work on complex, multi-billion dollar deals that shape industries.',
        responsibilities: [
          'Draft and negotiate transaction documents',
          'Conduct legal due diligence',
          'Advise clients on regulatory and compliance matters',
          'Coordinate with other legal and advisory teams',
          'Manage junior associates and support staff',
          'Develop and maintain client relationships'
        ],
        requirements: [
          'JD degree from an accredited law school',
          'Active bar membership',
          '3-6 years of experience in corporate law with M&A focus',
          'Strong drafting and negotiation skills',
          'Experience with public and private M&A transactions'
        ],
        benefits: [
          'Market-leading compensation package',
          'Comprehensive health and wellness benefits',
          'Professional development opportunities',
          'Sabbatical program for senior associates',
          'Pro bono and community service opportunities'
        ],
        postedDate: '2025-04-01',
        applicationDeadline: '2025-05-01',
        featured: true,
        contactPerson: 'Legal Recruiting',
        contactEmail: 'recruiting@skadden.com'
      },
      {
        id: 8,
        title: 'Financial Due Diligence Senior Associate',
        company: 'PwC',
        location: 'San Francisco, CA',
        type: 'Full-time',
        salary: '$100,000 - $130,000',
        description: 'PwC is seeking a Senior Associate to join our Financial Due Diligence team. The role involves conducting financial due diligence for M&A transactions, including quality of earnings analyses and working capital assessments.',
        detailedDescription: 'As a Senior Associate in our Financial Due Diligence practice, you will lead workstreams on complex M&A transactions, working directly with private equity firms, strategic acquirers, and investment banks. You will develop deep expertise in financial analysis and transaction support.',
        responsibilities: [
          'Lead financial due diligence workstreams',
          'Perform quality of earnings analyses',
          'Conduct working capital and cash flow assessments',
          'Prepare client presentations and reports',
          'Manage and develop junior team members',
          'Interface directly with clients and other advisors'
        ],
        requirements: [
          'Bachelor\'s degree in Accounting, Finance, or related field',
          'CPA certification preferred',
          '3-5 years of experience in transaction services or audit',
          'Strong analytical and communication skills',
          'Experience with financial due diligence preferred'
        ],
        benefits: [
          'Competitive salary and performance-based bonus',
          'Comprehensive benefits and wellness programs',
          'Professional development and training opportunities',
          'Flexible work arrangements',
          'Global mobility opportunities'
        ],
        postedDate: '2025-03-30',
        applicationDeadline: '2025-04-30',
        featured: false,
        contactPerson: 'Transaction Services Recruiting',
        contactEmail: 'deals.careers@pwc.com'
      }
    ];

    // Find the job with the matching ID
    const foundJob = jobs.find(j => j.id === parseInt(id));
    
    // Simulate API call
    setTimeout(() => {
      setJob(foundJob);
      setLoading(false);
    }, 500);
  }, [id]);

  const handleApplyInputChange = (e) => {
    const { name, value } = e.target;
    setApplication({
      ...application,
      [name]: value
    });
  };

  const handleFileChange = (e) => {
    setApplication({
      ...application,
      resume: e.target.files[0]
    });
  };

  const handleReferInputChange = (e) => {
    const { name, value } = e.target;
    setReferral({
      ...referral,
      [name]: value
    });
  };

  const handleApplySubmit = (e) => {
    e.preventDefault();
    // In a real app, this would send the application to an API
    console.log('Application submitted:', application);
    alert('Your application has been submitted successfully!');
    setShowApplyModal(false);
    // Reset form
    setApplication({
      coverLetter: '',
      resume: null
    });
  };

  const handleReferSubmit = (e) => {
    e.preventDefault();
    // In a real app, this would send the referral to an API
    console.log('Referral submitted:', referral);
    alert('Your referral has been submitted successfully!');
    setShowReferModal(false);
    // Reset form
    setReferral({
      name: '',
      email: '',
      relationship: '',
      note: ''
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="bg-white min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">Job Not Found</h1>
            <p className="mt-4 text-lg text-gray-600">The job you're looking for doesn't exist or has been removed.</p>
            <Link to="/jobs" className="mt-6 inline-block bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700 transition duration-200">
              Back to Job Board
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">{job.title}</h1>
            <p className="mt-2 text-lg">{job.company} • {job.location}</p>
          </div>
        </div>
      </div>

      {/* Job Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Job Header */}
            <div className="bg-gray-50 p-6 border-b border-gray-200">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                <div>
                  <div className="flex items-center">
                    <h2 className="text-2xl font-bold text-gray-900">{job.title}</h2>
                    {job.featured && (
                      <span className="ml-2 inline-block bg-apn-blue text-white text-xs px-2 py-1 rounded">
                        Featured
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 mt-1">{job.company} • {job.location}</p>
                </div>
                <div className="mt-4 md:mt-0 flex space-x-4">
                  <button
                    onClick={() => setShowApplyModal(true)}
                    className="inline-flex items-center bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700 transition duration-200"
                  >
                    Apply Now
                  </button>
                  <button
                    onClick={() => setShowReferModal(true)}
                    className="inline-flex items-center bg-white text-apn-blue border border-apn-blue py-2 px-6 rounded hover:bg-gray-50 transition duration-200"
                  >
                    Refer Someone
                  </button>
                </div>
              </div>
            </div>

            {/* Job Details */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Job Type</h3>
                  <p className="text-gray-700">{job.type}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Location</h3>
                  <p className="text-gray-700">{job.location}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Salary</h3>
                  <p className="text-gray-700">{job.salary}</p>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Job Description</h3>
                <p className="text-gray-700 mb-4">{job.description}</p>
                <p className="text-gray-700">{job.detailedDescription}</p>
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Responsibilities</h3>
                <ul className="list-disc pl-5 text-gray-700 space-y-2">
                  {job.responsibilities.map((responsibility, index) => (
                    <li key={index}>{responsibility}</li>
                  ))}
                </ul>
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Requirements</h3>
                <ul className="list-disc pl-5 text-gray-700 space-y-2">
                  {job.requirements.map((requirement, index) => (
                    <li key={index}>{requirement}</li>
                  ))}
                </ul>
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Benefits</h3>
                <ul className="list-disc pl-5 text-gray-700 space-y-2">
                  {job.benefits.map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                  <div>
                    <p className="text-gray-600">Posted on {new Date(job.postedDate).toLocaleDateString()}</p>
                    <p className="text-gray-600">Application Deadline: {new Date(job.applicationDeadline).toLocaleDateString()}</p>
                  </div>
                  <div className="mt-4 md:mt-0 flex space-x-4">
                    <button
                      onClick={() => setShowApplyModal(true)}
                      className="inline-flex items-center bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700 transition duration-200"
                    >
                      Apply Now
                    </button>
                    <button
                      onClick={() => setShowReferModal(true)}
                      className="inline-flex items-center bg-white text-apn-blue border border-apn-blue py-2 px-6 rounded hover:bg-gray-50 transition duration-200"
                    >
                      Refer Someone
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Apply Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Apply for Job</h2>
                <button
                  onClick={() => setShowApplyModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-gray-600 mb-6">
                You are applying for: <span className="font-semibold">{job.title}</span> at <span className="font-semibold">{job.company}</span>
              </p>
              <form onSubmit={handleApplySubmit}>
                <div className="mb-4">
                  <label htmlFor="coverLetter" className="block text-sm font-medium text-gray-700 mb-1">
                    Cover Letter
                  </label>
                  <textarea
                    id="coverLetter"
                    name="coverLetter"
                    rows="5"
                    value={application.coverLetter}
                    onChange={handleApplyInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Introduce yourself and explain why you're a good fit for this position..."
                  ></textarea>
                </div>
                <div className="mb-6">
                  <label htmlFor="resume" className="block text-sm font-medium text-gray-700 mb-1">
                    Resume / CV (PDF)
                  </label>
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    accept=".pdf"
                    onChange={handleFileChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                  />
                </div>
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowApplyModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700"
                  >
                    Submit Application
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Refer Modal */}
      {showReferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Refer Someone for this Job</h2>
                <button
                  onClick={() => setShowReferModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-gray-600 mb-6">
                You are referring someone for: <span className="font-semibold">{job.title}</span> at <span className="font-semibold">{job.company}</span>
              </p>
              <form onSubmit={handleReferSubmit}>
                <div className="mb-4">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Referral's Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={referral.name}
                    onChange={handleReferInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Full name of the person you're referring"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Referral's Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={referral.email}
                    onChange={handleReferInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Email address of the person you're referring"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="relationship" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Relationship
                  </label>
                  <select
                    id="relationship"
                    name="relationship"
                    value={referral.relationship}
                    onChange={handleReferInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                  >
                    <option value="">Select your relationship</option>
                    <option value="Colleague">Colleague</option>
                    <option value="Classmate">Classmate</option>
                    <option value="Friend">Friend</option>
                    <option value="Professional Connection">Professional Connection</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="mb-6">
                  <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
                    Referral Note
                  </label>
                  <textarea
                    id="note"
                    name="note"
                    rows="4"
                    value={referral.note}
                    onChange={handleReferInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Why do you think this person would be a good fit for the job?"
                  ></textarea>
                </div>
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowReferModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700"
                  >
                    Submit Referral
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Similar Jobs */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Similar Jobs</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <h3 className="font-bold text-lg mb-2">Senior Investment Banking Associate</h3>
                <p className="text-gray-600 text-sm mb-2">Morgan Stanley • New York, NY</p>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">Join our M&A team to work on high-profile transactions across various industries.</p>
                <div className="flex justify-between text-xs text-gray-500 mb-4">
                  <span>Full-time</span>
                  <span>$140,000 - $180,000</span>
                </div>
                <Link to="/jobs/3" className="text-apn-blue hover:underline text-sm">
                  View Details →
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <h3 className="font-bold text-lg mb-2">Investment Banking Analyst</h3>
                <p className="text-gray-600 text-sm mb-2">JP Morgan • New York, NY</p>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">Entry-level position for recent graduates to join our investment banking division.</p>
                <div className="flex justify-between text-xs text-gray-500 mb-4">
                  <span>Full-time</span>
                  <span>$90,000 - $110,000</span>
                </div>
                <Link to="/jobs/4" className="text-apn-blue hover:underline text-sm">
                  View Details →
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6">
                <h3 className="font-bold text-lg mb-2">Private Equity Senior Associate</h3>
                <p className="text-gray-600 text-sm mb-2">KKR • New York, NY</p>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">Experienced professional to join our private equity team focusing on technology investments.</p>
                <div className="flex justify-between text-xs text-gray-500 mb-4">
                  <span>Full-time</span>
                  <span>$150,000 - $200,000</span>
                </div>
                <Link to="/jobs/5" className="text-apn-blue hover:underline text-sm">
                  View Details →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Find More Jobs</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Browse our job board to find more opportunities that match your skills and career goals.
          </p>
          <Link to="/jobs" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            View All Jobs
          </Link>
        </div>
      </section>
    </div>
  );
};

export default JobDetail;
